/**
 * Bloco de edição de uma contestada na pré-visualização do laudo. // ux-neutrality:allow doc-comment
 *
 * E23.F20 (ADR-0030): o documento renderizado (PDF) passou a mostrar o texto // ux-neutrality:allow doc-comment
 * "como sai"; este bloco fica só com o editor conversacional — o perito // ux-neutrality:allow doc-comment
 * descreve a alteração, o Agente propõe, o perito confirma inline (E23.F8). // ux-neutrality:allow doc-comment
 */
import { useCallback, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { LaudoError } from '@/lib/laudo';
import {
  confirmLaudoEdit,
  proposeLaudoEdit,
  type LaudoEditProposalData,
  type LaudoPreviewContestadaData,
} from '@/lib/laudo_edits';
import { LaudoEditProposalCard } from '@/components/LaudoEditProposalCard';
import { LaudoManualFieldEditor } from '@/components/LaudoManualFieldEditor';
import { Badge, Blueprint, Button, CardKicker, Textarea } from '@/components/ui';

interface LaudoPreviewContestadaProps {
  caseId: string;
  contestada: LaudoPreviewContestadaData;
  isOwner: boolean;
  onEdited: () => void;
}

function ConversationalEditor({
  caseId,
  contestada,
  onEdited,
}: {
  caseId: string;
  contestada: LaudoPreviewContestadaData;
  onEdited: () => void;
}): JSX.Element {
  const t = ptBR.laudoCaso.preview;
  const client = useApiClient();
  const [instruction, setInstruction] = useState('');
  const [proposing, setProposing] = useState(false);
  const [proposal, setProposal] = useState<LaudoEditProposalData | null>(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const handlePropose = useCallback(async () => {
    if (!instruction.trim()) return;
    setProposing(true);
    setError(null);
    try {
      const result = await proposeLaudoEdit(
        client,
        caseId,
        contestada.case_analysis_id,
        instruction.trim(),
      );
      setProposal(result);
    } catch (err) {
      const reason = err instanceof LaudoError ? err.reason : 'unknown';
      setError(reason === 'assistant_timeout' ? t.proposeErrorTimeout : t.proposeError);
    } finally {
      setProposing(false);
    }
  }, [client, caseId, contestada.case_analysis_id, instruction, t]);

  const handleApprove = useCallback(async () => {
    if (!proposal) return;
    setApplying(true);
    setApplyError(null);
    try {
      const trimmed = instruction.trim();
      await confirmLaudoEdit(client, caseId, {
        caseAnalysisId: contestada.case_analysis_id,
        targetField: proposal.target_field,
        newValue: proposal.proposed_value,
        source: 'agent_proposed',
        ...(trimmed ? { justification: trimmed } : {}),
      });
      setProposal(null);
      setInstruction('');
      onEdited();
    } catch (err) {
      const reason = err instanceof LaudoError ? err.reason : 'unknown';
      setApplyError(
        reason === 'narrative_already_approved'
          ? ptBR.laudoCaso.editProposal.applyErrorApproved
          : ptBR.laudoCaso.editProposal.applyError,
      );
    } finally {
      setApplying(false);
    }
  }, [client, caseId, contestada.case_analysis_id, proposal, instruction, onEdited]);

  const proposedCurrent = proposal
    ? ((contestada[proposal.target_field] as string | null) ?? '')
    : '';

  return (
    <div className="mt-sc-3">
      <label
        className="text-caption font-medium text-fg"
        htmlFor={`laudo-edit-instruction-${contestada.letra}`}
      >
        {t.instructionLabel}
      </label>
      <Textarea
        id={`laudo-edit-instruction-${contestada.letra}`}
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        placeholder={t.instructionPlaceholder}
        rows={2}
        className="mt-1"
        data-testid={`laudo-edit-instruction-${contestada.letra}`}
      />
      {error && (
        <p role="alert" className="mt-1 text-caption text-danger-700">
          {error}
        </p>
      )}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => void handlePropose()}
        disabled={proposing || !instruction.trim()}
        className="mt-sc-2"
        data-testid={`laudo-edit-propose-${contestada.letra}`}
      >
        {proposing ? t.proposing : t.proposeCta}
      </Button>
      {proposal && (
        <LaudoEditProposalCard
          proposal={proposal}
          currentValue={proposedCurrent}
          applying={applying}
          errorMessage={applyError}
          onApprove={() => void handleApprove()}
          onDiscard={() => {
            setProposal(null);
            setApplyError(null);
          }}
        />
      )}
    </div>
  );
}

export function LaudoPreviewContestada({
  caseId,
  contestada,
  isOwner,
  onEdited,
}: LaudoPreviewContestadaProps): JSX.Element {
  const t = ptBR.laudoCaso.preview;
  return (
    <Blueprint
      as="section"
      data-testid={`laudo-preview-contestada-${contestada.letra}`}
      data-contestada-letra={contestada.letra}
      className="p-sc-4"
    >
      <header className="flex items-center gap-sc-2">
        <CardKicker className="block">{t.editSectionKicker}</CardKicker>
        <h3 className="text-body-md font-semibold text-fg">
          {t.contestadaHeading.replace('{{letra}}', `${contestada.letra}) ${contestada.label}`)}
        </h3>
        <Badge
          variant={contestada.approved ? 'success' : 'neutral'}
          data-testid={`laudo-preview-badge-${contestada.letra}`}
        >
          {contestada.approved ? t.badgeApproved : t.badgeDraft}
        </Badge>
      </header>

      {!contestada.narrative_present ? (
        <p className="mt-sc-2 text-body-sm text-fg-muted">{t.empty}</p>
      ) : (
        <>
          {!isOwner && (
            <p className="mt-sc-3 text-caption text-fg-muted" role="note">
              {t.readOnlyForMember}
            </p>
          )}
          {isOwner && contestada.approved && (
            <p className="mt-sc-3 text-caption text-fg-muted" role="note">
              {t.approvedLocked}
            </p>
          )}
          {isOwner && !contestada.approved && (
            <>
              <ConversationalEditor caseId={caseId} contestada={contestada} onEdited={onEdited} />
              <LaudoManualFieldEditor caseId={caseId} contestada={contestada} onEdited={onEdited} />
            </>
          )}
        </>
      )}
    </Blueprint>
  );
}
